import { first, groupBy, isArray, isEmpty } from 'lodash'
import {
  defaultExportMenu,
  getColumnSeries,
  getGroupedSeries,
  getLineSeries,
  getStackedSeries
} from '../helpers'

const seriesConfig = ({ el, otherConfig }) => {
  const { type, config } = el
  switch (type) {
    case 'grouped':
      return getGroupedSeries({ series: config, otherConfig })
    case 'line':
      return getLineSeries({ series: config, otherConfig })
    case 'stacked':
      return getStackedSeries({ series: config, otherConfig })
    case 'column':
    default:
      return getColumnSeries({ series: config, otherConfig })
  }
}

const yAxesProcess = ({ series, title, secondAxesTitle, sync, labelColor }) => {
  let yAxes = []
  const yAxisGrouped = groupBy(series, 'yAxis')
  const yAxisKeys = Object.keys(yAxisGrouped)

  if (sync && yAxisKeys.length !== 1) {
    yAxes = yAxisKeys.map((el, index) => {
      if (yAxisKeys.length - 1 === index) {
        return {
          id: el,
          type: 'ValueAxis',
          syncWithAxis: first(yAxisKeys),
          title: {
            text: secondAxesTitle || null,
            fill: labelColor
          },
          renderer: {
            opposite: true,
            line: {
              fill: labelColor,
              strokeOpacity: 0.3
            },
            labels: {
              fill: labelColor
            },
            paddingRight: -9
          }
        }
      } else {
        return {
          id: el,
          type: 'ValueAxis',
          title: {
            text: title || null,
            fill: labelColor
          },
          renderer: {
            line: {
              fill: labelColor,
              strokeOpacity: 0.3
            },
            labels: {
              fill: labelColor
            }
          }
        }
      }
    })
  } else {
    yAxes = [
      {
        title: {
          text: title || null,
          fill: labelColor
        },
        id: 'CommonYAxis',
        type: 'ValueAxis',
        renderer: {
          line: {
            strokeOpacity: 0.3,
            fill: labelColor
          },
          labels: {
            fill: labelColor
          }
        }
      },
      {
        type: 'ValueAxis',
        renderer: {
          opposite: true,
          line: {
            strokeOpacity: 0,
            fill: labelColor
          },
          labels: {
            fill: labelColor
          }
        }
      }
    ]
  }
  return yAxes
}

export default ({ config, data, color, legendLabels = {} }) => {
  const { series, ...restConfig } = config
  const {
    title,
    xTitle,
    xKey,
    scrollbar,
    opposite,
    secondAxesTitle,
    sync,
    axisRanges = [],
    exportingMenu,
    disabledAnimations = false
  } = restConfig
  const { labelColor } = color
  const seriesList = []

  series.forEach((el) => {
    const keys = []
    // get custom legend labels with translated values
    Object.keys(legendLabels).forEach((key) => {
      if (el.config.includes(key)) {
        keys.push(legendLabels[key])
      }
    })

    const configSeries = seriesConfig({
      el,
      otherConfig: {
        legendLabel: keys,
        color,
        disabledAnimations,
        ...restConfig
      }
    })

    if (isArray(configSeries)) {
      seriesList.push(...configSeries)
    } else {
      seriesList.push(configSeries)
    }
  })

  const configuration = {
    data: data,
    type: 'XYChart',
    series: seriesList,
    yAxes: yAxesProcess({
      series: seriesList,
      title,
      secondAxesTitle,
      opposite,
      sync,
      labelColor
    }),
    cursor: {},
    xAxes: [
      {
        type: 'CategoryAxis',
        dataFields: {
          category: xKey
        },
        rangeChangeDuration: 500,
        title: {
          text: xTitle || null,
          fill: labelColor
        },
        renderer: {
          labels: {
            rotation: -60,
            verticalCenter: 'left',
            horizontalCenter: 'right',
            fill: labelColor
          },
          grid: {
            template: {
              strokeWidth: 0
            }
          },
          line: {
            strokeOpacity: 0.3
          },
          minGridDistance: 60,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          maxLabelPosition: 0.98
        },
        // AxisRanges lets to create ranges with different backgrounds
        axisRanges: [
          ...axisRanges.map(
            ({
              category,
              endCategory,
              text,
              disabledLabels = false,
              paddingTop,
              dx,
              dy,
              fill
            }) => ({
              category: category,
              endCategory: endCategory,
              label: {
                disabled: disabledLabels,
                text,
                paddingTop: paddingTop || 10,
                location: 0.5,
                horizontalCenter: 'middle',
                fontSize: 12,
                fontWeight: 'bolder',
                fill: labelColor,
                dy: dy || 0,
                dx: dx || 0
              },
              axisFill: {
                fill: fill || '#434343',
                fillOpacity: 0.3
              }
            })
          )
        ]
      }
    ],
    scrollbarX: {
      disabled: !scrollbar,
      animationDuration: 500,
      parent: 'bottomAxesContainer'
    },

    legend: {
      useDefaultMarker: true,
      labels: {
        text: '{name}',
        fill: labelColor
      },
      contentAlign: 'left',
      paddingTop: 10,
      paddingLeft: 45,
      markers: {
        children: [
          {
            width: 20,
            height: 20,
            fontSize: 200,
            strokeWidth: 0,
            paddingTop: 2.5,
            marginLeft: 22.5,
            paddingLeft: 2.5,
            strokeOpacity: 0,
            paddingRight: 2.5,
            paddingBottom: 2.5,
            cornerRadiusTopLeft: 0,
            cornerRadiusTopRight: 0,
            cornerRadiusBottomLeft: 0,
            cornerRadiusBottomRight: 0
          }
        ]
      }
    }
  }

  // Export menu
  if (exportingMenu && exportingMenu.enabled) {
    if (isEmpty(exportingMenu.config)) {
      configuration.exporting = defaultExportMenu
    } else {
      configuration.exporting = exportingMenu.config
    }
  }

  return configuration
}
